import { NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface TabItem {
  to: string;
  label: string;
  count?: number;
  icon?: ReactNode;
  end?: boolean;
}

/** Linear/Vercel style tab bar — sits directly under H1. */
export function TabBar({ tabs }: { tabs: TabItem[] }) {
  const { pathname } = useLocation();
  return (
    <nav className="border-b border-border bg-bg-elevated -mt-px sticky top-0 z-10">
      <ul className="px-4 md:px-6 lg:px-8 flex items-center gap-1 flex-wrap">
        {tabs.map(tab => {
          const active = tab.end ? pathname === tab.to : pathname.startsWith(tab.to);
          return (
            <li key={tab.to}>
              <NavLink
                to={tab.to}
                end={tab.end}
                className={cn(
                  'inline-flex items-center gap-1.5 h-9 px-3 -mb-px text-sm transition-colors border-b-2 whitespace-nowrap',
                  active
                    ? 'text-fg font-semibold border-accent'
                    : 'text-fg-muted hover:text-fg border-transparent',
                )}
              >
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && (
                  <span className={cn(
                    'tabnum text-2xs h-4 min-w-4 px-1 rounded inline-flex items-center justify-center',
                    active ? 'bg-accent/10 text-accent' : 'bg-surface-2 text-fg-subtle',
                  )}>
                    {tab.count}
                  </span>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

interface PropertyChip {
  label: string;
  value: ReactNode;
  href?: string;
  mono?: boolean;
}

/** Property strip directly under H1. Linear pattern. */
export function PropertyChips({ chips }: { chips: PropertyChip[] }) {
  return (
    <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap text-xs">
      {chips.map((c, i) => (
        <div key={i} className="inline-flex items-center gap-1.5">
          <span className="text-fg-subtle">{c.label}</span>
          <span className={cn('text-fg font-medium', c.mono && 'font-mono')}>{c.value}</span>
        </div>
      ))}
    </div>
  );
}
